import { useCallback, useEffect, useRef, useState } from 'react';
import useDataAssistant from '../../../customHooks/useDataAssistant';
import PROMPT_KEYS from '../../../utils/commonVariables/promptKeys';
import { Competitor } from '../types/competitors';
import {
    mergeSuggestedCompetitors,
    parseSuggestedCompetitors,
} from '../utils/competitorSuggestions';
import { useNotification } from '../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';

interface CompetitorSuggestionsOptions {
    competitors: Competitor[];
    saveCompetitors: (competitors: Competitor[]) => Promise<void>;
}

interface CompetitorSuggestionParameters {
    hotelName: string;
    city: string;
    country: string;
    starRating: string;
    segment: string;
    excludedHotels: string;
}

/**
 * Calls the competitor suggestion prompt using the shared data assistant.
 * @param options Existing competitors and the scoped candidate persistence action.
 * @returns Suggestion action, raw answer, combined request/save status and errors.
 */
export const useCompetitorSuggestions = (
    options: CompetitorSuggestionsOptions
) => {
    const { setErrorMessage } = useNotification();
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const notifyRef = useRef(setErrorMessage);
    notifyRef.current = setErrorMessage;
    const mountedRef = useRef(false);
    const processedAnswerRef = useRef('');
    const candidatesRef = useRef<{
        answer: string;
        competitors: Competitor[];
    } | null>(null);
    const processingRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const {
        isLoadingAssistant,
        askQuestion,
        answer,
        assistantError,
        resetAssistant,
    } = useDataAssistant(
        PROMPT_KEYS.COMPETITOR_HOTEL_SUGGESTER_WITH_EXCLUSIONS
    );

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const processAnswer = useCallback(async (response: string) => {
        if (!mountedRef.current || processingRef.current) {
            return;
        }
        processingRef.current = true;
        setIsSaving(true);
        try {
            let suggestions: Competitor[];
            try {
                if (candidatesRef.current?.answer !== response) {
                    candidatesRef.current = {
                        answer: response,
                        competitors: parseSuggestedCompetitors(response).map(
                            suggestion => ({
                                ...suggestion,
                                id: crypto.randomUUID(),
                            })
                        ),
                    };
                }
                suggestions = mergeSuggestedCompetitors(
                    candidatesRef.current.competitors,
                    optionsRef.current.competitors
                );
            } catch (error) {
                console.error('Could not parse competitor suggestions:', error);
                notifyRef.current(FeedbackMessages.COMMON_ERROR);
                return;
            }
            try {
                if (suggestions.length > 0) {
                    await optionsRef.current.saveCompetitors(suggestions);
                }
                if (mountedRef.current) {
                    processedAnswerRef.current = response;
                }
            } catch (error) {
                // Persistence already reports the failure; keep the answer retryable.
                console.error('Could not save competitor suggestions:', error);
            }
        } finally {
            processingRef.current = false;
            if (mountedRef.current) {
                setIsSaving(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!answer) {
            processedAnswerRef.current = '';
            candidatesRef.current = null;
        } else if (processedAnswerRef.current !== answer) {
            void processAnswer(answer);
        }
    }, [answer, processAnswer]);

    useEffect(() => {
        if (assistantError) {
            notifyRef.current(assistantError);
        }
    }, [assistantError]);

    const suggestCompetitors = useCallback(
        async (parameters: CompetitorSuggestionParameters) => {
            if (processingRef.current || isLoadingAssistant) {
                return;
            }
            if (answer && processedAnswerRef.current !== answer) {
                await processAnswer(answer);
                return;
            }
            await askQuestion('', parameters);
        },
        [answer, askQuestion, isLoadingAssistant, processAnswer]
    );

    return {
        suggestCompetitors,
        isLoadingAssistant: isLoadingAssistant || isSaving,
        answer,
        assistantError,
        resetAssistant,
    };
};
